import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, CheckCircle2, Sparkles, X, Loader2 } from 'lucide-react';

export interface LoginSuccessToastProps {
  isOpen: boolean;
  onClose: () => void;
  userName?: string;
  actionName?: string | null;
  notice?: string | null;
  duration?: number;
}

export const LoginSuccessToast: React.FC<LoginSuccessToastProps> = ({
  isOpen,
  onClose,
  userName = 'Patron Client',
  actionName = null,
  notice = null,
  duration = 5000,
}) => {
  const [isProcessing, setIsProcessing] = useState(true);

  // Derive a clean, human-readable action label from actionName or notice
  const cleanActionLabel = React.useMemo(() => {
    if (actionName) return actionName;
    if (!notice) return null;

    // Standardize notice sentences into clean action verbs
    let text = notice.replace(/^Sign in or create an account to /i, '');
    text = text.replace(/^Sign in or create an account /i, '');
    text = text.replace(/\.$/, '');
    if (!text) return null;
    return text.charAt(0).toUpperCase() + text.slice(1);
  }, [actionName, notice]);

  useEffect(() => {
    if (isOpen) {
      setIsProcessing(true);

      // Transition from "Processing previous action..." to "Action completed" after 1.1s
      const processTimer = setTimeout(() => {
        setIsProcessing(false);
      }, 1100);

      // Auto dismiss after total duration
      const dismissTimer = setTimeout(() => {
        onClose();
      }, duration);

      return () => {
        clearTimeout(processTimer);
        clearTimeout(dismissTimer);
      };
    }
  }, [isOpen, duration, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed top-5 right-4 sm:top-8 sm:right-8 z-[999] max-w-md w-[calc(100vw-2rem)] sm:w-auto pointer-events-auto">
          <motion.div
            initial={{ opacity: 0, y: -24, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-[#181513]/95 text-[#FAF7F2] border border-[#C8A45A]/40 rounded-2xl p-4 sm:p-5 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl overflow-hidden font-sans space-y-3"
          >
            {/* Top Golden Light Accent Bar */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C8A45A] to-transparent animate-pulse" />

            {/* Header Content */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start space-x-3">
                <div className="relative shrink-0 mt-0.5">
                  <div className="w-9 h-9 rounded-full bg-[#C8A45A]/15 border border-[#C8A45A]/40 text-[#C8A45A] flex items-center justify-center shadow-inner">
                    <ShieldCheck className="w-5 h-5 text-[#C8A45A]" />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[#181513] animate-ping" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[#181513]" />
                </div>

                <div className="space-y-0.5">
                  <div className="flex items-center space-x-2">
                    <span className="text-[10px] uppercase font-serif tracking-[0.2em] font-bold text-[#C8A45A]">
                      Atelier Sanctuary
                    </span>
                    <span className="px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-400 text-[9px] font-bold uppercase tracking-wider border border-emerald-500/30">
                      Authenticated
                    </span>
                  </div>
                  <h4 className="font-serif font-bold text-sm text-[#FAF7F2] leading-tight">
                    Welcome back, {userName}
                  </h4>
                  <p className="text-[11px] text-[#A3978C] font-serif italic">
                    Successfully signed in to your account.
                  </p>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-1 rounded-lg text-[#8C7E72] hover:text-[#FAF7F2] hover:bg-[#C8A45A]/10 transition-colors cursor-pointer shrink-0"
                aria-label="Dismiss notification"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Action Processing Visual Indicator Banner */}
            {cleanActionLabel ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="pt-2 border-t border-[#C8A45A]/20"
              >
                {isProcessing ? (
                  <div className="p-2.5 rounded-xl bg-[#241F1C] border border-[#C8A45A]/30 text-xs flex items-center justify-between gap-2 shadow-xs">
                    <div className="flex items-center space-x-2.5 min-w-0">
                      <Loader2 className="w-4 h-4 text-[#C8A45A] animate-spin shrink-0" />
                      <div className="truncate">
                        <span className="text-[10px] uppercase tracking-wider text-[#A3978C] font-bold block">
                          Executing Previous Action
                        </span>
                        <span className="text-xs font-medium text-[#FAF7F2] truncate block">
                          {cleanActionLabel}
                        </span>
                      </div>
                    </div>
                    <span className="text-[10px] text-[#C8A45A] font-serif italic shrink-0 animate-pulse">
                      Processing...
                    </span>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-2.5 rounded-xl bg-emerald-950/40 border border-emerald-500/40 text-xs flex items-center justify-between gap-2 shadow-xs"
                  >
                    <div className="flex items-center space-x-2.5 min-w-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <div className="truncate">
                        <span className="text-[10px] uppercase tracking-wider text-emerald-400/80 font-bold block">
                          Action Processed
                        </span>
                        <span className="text-xs font-semibold text-emerald-100 truncate block">
                          {cleanActionLabel}
                        </span>
                      </div>
                    </div>
                    <span className="text-[10px] text-emerald-400 font-serif font-bold uppercase tracking-wider shrink-0 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                      Done
                    </span>
                  </motion.div>
                )}
              </motion.div>
            ) : (
              <div className="pt-2 border-t border-[#C8A45A]/20 flex items-center justify-between text-[11px] text-[#C8A45A] font-serif">
                <span className="flex items-center space-x-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>All Patron privileges and saved preferences are active</span>
                </span>
              </div>
            )}

            {/* Bottom Shrinking Timer Progress Line */}
            <div className="w-full bg-[#241F1C] h-1 rounded-full overflow-hidden mt-1">
              <motion.div
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: duration / 1000, ease: 'linear' }}
                className="h-full bg-gradient-to-r from-[#C8A45A] to-emerald-400 rounded-full"
              />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
